import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { PenIcon } from "../../Icons";
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import EvaluationSelect from "../EvaluationNew/components/EvaluationSelect";
import { fluVaccinationSchema } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import SelectionGroup from "../../components/Application/SelectionGroup";

const FLU_ISO_DATE_FIELDS = ["applicantSignedAt", "reviewedBySignedAt"];

const FluVaccination = ({ positionName, onClose }) => {
  const [vaccinationId, setVaccinationId] = useState(null);

  const methods = useForm({
    resolver: zodResolver(fluVaccinationSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      clinicianName: "",
      clinicianTitle: "",
      alreadyReceivedVaccination: false,
      requestVaccinationHomeHealth: false,
      refuseVaccination: false,
      refusalReason: "",
      refusalAcknowledgmentSignature: "",
      refusalAcknowledgmentDate: "",
      fluVaccinationConsentPrintName: "",
      clinicianSignature: "",
      locationProvidingVaccine: "",
      siteOfInjection: "",
      administeredByType: "",
      administeredByName: "",
      lotNumber: "",
      expirationDate: "",
      applicantSignature: "",
      applicantSignedAt: "",
      applicantSignedByUserId: 1,
      positionAppliedFor: positionName || "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: ""
    },
    mode: "onChange"
  });

  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      // Clean up payload based on selection to avoid enum validation errors
      let cleanedData = { ...data };

      if (data.refuseVaccination) {
        // Remove vaccination admin fields if refusing
        const fieldsToRemove = [
          'clinicianName', 'clinicianTitle', 'clinicianSignature',
          'locationProvidingVaccine', 'siteOfInjection', 'administeredByType',
          'administeredByName', 'lotNumber', 'expirationDate', 'fluVaccinationConsentPrintName'
        ];
        fieldsToRemove.forEach(field => delete cleanedData[field]);

        cleanedData.alreadyReceivedVaccination = false;
        cleanedData.requestVaccinationHomeHealth = false;
      } else {
        // Remove refusal fields if consenting
        delete cleanedData.refusalReason;
        delete cleanedData.refusalAcknowledgmentSignature;
        delete cleanedData.refusalAcknowledgmentDate;
        cleanedData.refuseVaccination = false;

        // Ensure administeredByType is either a valid value or removed
        if (!cleanedData.administeredByType) {
          delete cleanedData.administeredByType;
        }
      }

      const payload = buildPayloadWithIsoDates(
        cleanedData,
        additionalData,
        FLU_ISO_DATE_FIELDS,
        { flag: actualFlag }
      );
      payload.expirationDate = payload.expirationDate
        ? toIsoDate(payload.expirationDate)
        : undefined;
      payload.refusalAcknowledgmentDate = payload.refusalAcknowledgmentDate
        ? toIsoDate(payload.refusalAcknowledgmentDate)
        : undefined;

      // Backend rejects "" for optional name fields (invalid_format); omit when blank
      if (!String(payload.fluVaccinationConsentPrintName ?? "").trim()) {
        delete payload.fluVaccinationConsentPrintName;
      }
      if (!String(payload.administeredByName ?? "").trim()) {
        delete payload.administeredByName;
      }

      // Handle To Be Corrected comments
      const finalComments =
        payload.comments || additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const method = vaccinationId ? 'PUT' : 'POST';
      const url = vaccinationId ? `/flu-vaccinations/${vaccinationId}` : '/flu-vaccinations';

      const response = await request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.status === 1) {
        toast.success(response.message || "Flu Vaccination form processed successfully!");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,

    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    adminNameField: "reviewedBySignedBy",
    onClose,
    onSubmit,
    currentStatus
  });

  const { handleSubmit, setValue, watch, reset } = methods;
  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchFluVaccination = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId) return;

      try {
        if (!actualFromId && !targetUserId && !vaccinationId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              positionAppliedFor: positionName || "",
            });
          }
          return;
        }


        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/flu-vaccinations/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/flu-vaccinations?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/flu-vaccinations/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) data = response.result[0];
          } else if (response.result?.vaccination) {
            data = response.result.vaccination;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId, 10));
            }
          }
        }

        if (data) {
          setVaccinationId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            expirationDate: parseIsoDateToMDYUS(data.expirationDate) || getLocalSystemDate(),
            refusalAcknowledgmentDate: parseIsoDateToMDYUS(data.refusalAcknowledgmentDate) || getLocalSystemDate(),
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || "")
          });
        }
      } catch (error) {
        console.error("Error fetching flu vaccination:", error);
      }
    };
    fetchFluVaccination();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
  }, [targetUserId, currentUserData?.id, actualFromId]);


  const handleSignatureClick = (fieldName, type = 'applicant') => {
    openSignatureModal(type, {
      signatureField: fieldName,
      dateField: fieldName === "refusalAcknowledgmentSignature" ? "refusalAcknowledgmentDate" : null
    });
  };

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Flu Vaccination Consent / Declination" />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-8">
                {/* Employee Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EvaluationInput
                    name="employeeName"
                    label="Employee Name"
                    isRequired
                    rules={{
                      required: "Employee Name is required",
                      validate: (v) =>
                        (typeof v === "string" && v.trim().length > 0) ||
                        "Employee Name is required",
                    }}
                  />
                </div>

                <div className="space-y-6">
                  <SelectionGroup
                    label="Reason for Submission (Select One)"
                    options={[
                      {
                        name: "alreadyReceivedVaccination",
                        label: "I have already received the flu vaccination. (Please provide proof of vaccination)",
                      },
                      {
                        name: "requestVaccinationHomeHealth",
                        label: "I am requesting the flu vaccination from Home Health.",
                      },
                      {
                        name: "refuseVaccination",
                        label: "I refuse the flu vaccination. (Complete refusal section below)",
                      },
                    ]}
                    errorNames={["alreadyReceivedVaccination", "requestVaccinationHomeHealth", "refuseVaccination"]}
                    isReadOnly={isReadOnly}
                    className="space-y-6"
                    itemClassName="bg-gray-50 border border-gray-100 rounded-xl p-6"
                  />
                </div>

                {/* Refusal Section */}
                {watch("refuseVaccination") && (
                  <div className="mt-8 border border-red-100 bg-red-50/50 rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-red-50 px-6 py-4 border-b border-red-100 flex items-center gap-3">
                      <div className="bg-red-100 p-2 rounded-full text-red-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <h3 className="font-bold text-red-900 text-lg">
                        Vaccination Refusal Form
                      </h3>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="bg-white p-4 rounded-lg border border-red-100">
                        <EvaluationInput
                          name="refusalReason"
                          label="Reason for Refusal"
                          placeholder="Please provide a detailed reason for refusing the vaccination..."
                        />
                      </div>

                      <div className="border-t border-red-100 pt-6">
                        <label className="block text-sm font-bold text-red-900 mb-4 uppercase tracking-wide">
                          Refusal Acknowledgment Signature
                        </label>

                        {watch("refusalAcknowledgmentSignature") ? (
                          <div className="bg-white border border-red-200 rounded-xl p-4 flex justify-between items-center shadow-sm">
                            <div>
                              <p className="font-signature text-xl text-red-900">
                                {watch("refusalAcknowledgmentSignature")}
                              </p>
                              <p className="text-xs text-red-500 mt-1 font-medium">
                                Signed on{" "}
                                {new Date(
                                  watch("refusalAcknowledgmentDate"),
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-bold border border-red-100">
                              SIGNED
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              handleSignatureClick("refusalAcknowledgmentSignature")
                            }
                            className="w-full sm:w-auto flex items-center justify-center gap-3 bg-white border-2 border-red-200 text-red-700 px-6 py-3 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all font-semibold group"
                          >
                            <PenIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />
                            Sign Refusal Acknowledgement
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Consent Section (if requesting) */}
                {(watch("requestVaccinationHomeHealth") ||
                  watch("alreadyReceivedVaccination")) && (
                    <div className="mt-12 pt-8 border-t border-gray-200">
                      <div className="bg-blue-50/50 rounded-xl border border-blue-100 overflow-hidden">
                        <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
                          <h3 className="font-bold text-blue-900 text-lg">
                            Vaccination Details
                          </h3>
                          <p className="text-blue-600 text-sm">
                            To be completed by Clinician
                          </p>
                        </div>

                        <div className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <EvaluationInput
                              name="clinicianName"
                              label="Clinician Name"
                              placeholder="Full Name"
                              isRequired
                            />
                            <EvaluationInput
                              name="clinicianTitle"
                              label="Clinician Title"
                              placeholder="Title (e.g., RN, MD)"
                              isRequired
                            />
                            <EvaluationInput
                              name="locationProvidingVaccine"
                              label="Location Providing Vaccine"
                              isRequired
                            />
                            <EvaluationInput
                              name="siteOfInjection"
                              label="Site of Injection"
                              isRequired
                            />

                            <EvaluationSelect
                              name="administeredByType"
                              label="Administered By (Type)"
                              options={[
                                { value: "RN", label: "RN" },
                                { value: "MD", label: "MD" },
                              ]}
                              placeholder="Select Type..."
                              isRequired
                            />

                            <EvaluationInput
                              name="administeredByName"
                              label="Administered By (Name)"
                              isRequired
                            />
                            <EvaluationInput name="lotNumber" label="Lot Number" isRequired />
                            <InputDatePicker
                              formatSeparator="/"
                              name="expirationDate"
                              label="Expiration Date"
                              max="9999-12-31"
                              isRequired
                            />
                          </div>

                          <div className="bg-white p-4 rounded-lg border border-blue-100">
                            <EvaluationInput
                              name="clinicianSignature"
                              label="Clinician Signature"
                              placeholder="Type full name as signature"
                              isRequired
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </fieldset>
            </form>

            <ApplicationFormFooter
              page={1}
              totalPages={1}
              methods={methods}
              onSave={handleSave}
              onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
              onStatusUpdate={handleStatusAction}
              isSignatureModalOpen={isSignatureModalOpen}
              openSignatureModal={(type) => openSignatureModal(type)}
              closeSignatureModal={closeSignatureModal}
              handleSignatureApply={handleSignatureApply}
              signerName={signerName}
              applicantName={watch("employeeName") || ""}
              applicantSignature={watch("applicantSignature")}
              applicantSignedAt={watch("applicantSignedAt")}
              reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
              reviewedBySignature={watch("reviewedBySignature")}
              reviewedBySignedAt={watch("reviewedBySignedAt")}
              showCertification={false}
              submitLabel="Submit & Print"
              isReadOnly={isReadOnly}
              onClose={closeForm}
              currentStatus={currentStatus}
            />
          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default FluVaccination;
